import type { ReactNode } from 'react';
import { cn } from '../cn.ts';
import { Eyebrow, type EyebrowProps } from './Eyebrow.tsx';
import { Heading, type HeadingProps } from './Heading.tsx';
import { Text, type TextProps } from './Text.tsx';

/**
 * Đầu section: eyebrow → h2 → lead, đúng nhịp đo được của thiết kế (`reference.md` §3):
 * `margin-top` 14px cho h2, 16px cho lead; `max-width` 60ch khi canh trái, 52ch khi canh giữa.
 *
 * `align="center"` KHÔNG chỉ là `text-center`: nó đổi cả `max-width` và thêm `mx-auto`, vì
 * thiết kế dùng hai bề rộng khác nhau cho hai cách canh.
 *
 * `leadTone` mặc định là `default` (`ink-600`), KHÔNG phải `muted` (`ink-500`). Đo cả 5 lead trên
 * nền sáng của thiết kế (dòng 792, 816, 842, 951, 999) — tất cả đều cùng một mã xám của
 * `ink-600`. `reference.md` §3
 * từng ghi `ink-500`; đã sửa ở lô A′. Sai một bậc xám ở đây là sai ở MỌI section.
 */
export interface SectionHeadProps {
  readonly eyebrow: string;
  readonly heading: ReactNode;
  readonly lead?: ReactNode;
  readonly id: string;
  readonly align?: 'start' | 'center';
  readonly eyebrowTone?: EyebrowProps['tone'];
  readonly headingTone?: HeadingProps['tone'];
  readonly leadTone?: TextProps['tone'];
  readonly headingSize?: 'h2-large' | 'h2' | 'h2-compact' | 'display';
  /**
   * Thiết kế có HAI bậc lead: 17px và 16.5px. Nửa pixel nghe như không đáng, nhưng nhân với một
   * đoạn ba dòng thì đủ đổi chỗ ngắt dòng — cổng `design-diff` đo được ở `#pm`.
   */
  readonly leadSize?: 'lead' | 'lead-compact';
  /**
   * Bề rộng khối chữ tính bằng `ch`. Thiết kế KHÔNG dùng một giá trị: 60ch (`#modules`), 56ch
   * (`#pm`, `#knowledge`), 52ch (`#pricing`, canh giữa). Truyền số đo của section mình.
   *
   * Đừng đặt `max-w-…` qua `className`: `cn()` chỉ nối chuỗi nên hai utility `max-width` cùng tồn
   * tại và thắng thua do thứ tự Tailwind xếp trong CSS. Đo được: `#pm` truyền 56ch qua `className`
   * mà vẫn render 60ch.
   */
  readonly maxWidth?: '52ch' | '56ch' | '60ch' | '62ch';
  readonly className?: string;
}

const MAX_WIDTH = {
  '52ch': 'max-w-[52ch]',
  '56ch': 'max-w-[56ch]',
  '60ch': 'max-w-[60ch]',
  '62ch': 'max-w-[62ch]',
} as const;

export function SectionHead({
  eyebrow,
  heading,
  lead,
  id,
  align = 'start',
  eyebrowTone = 'brand',
  headingTone = 'default',
  leadTone = 'default',
  headingSize = 'h2',
  leadSize = 'lead',
  maxWidth,
  className,
}: SectionHeadProps) {
  const width = MAX_WIDTH[maxWidth ?? (align === 'center' ? '52ch' : '60ch')];
  return (
    <div className={cn(align === 'center' && 'mx-auto text-center', width, className)}>
      <Eyebrow tone={eyebrowTone}>{eyebrow}</Eyebrow>
      <Heading level={2} size={headingSize} tone={headingTone} id={id} className="mt-[14px]">
        {heading}
      </Heading>
      {lead !== undefined && (
        <Text size={leadSize} tone={leadTone} className="mt-4">
          {lead}
        </Text>
      )}
    </div>
  );
}
